/*
 * "deleted account with money due" made impossible to miss: the Teacher Payments banner, the Bank
 * payouts badge, the Deleted accounts section label and the teacher profile banner.
 */
import { fmt_money, ist_day_label } from '../format';
import { can_see_bank_details } from '../shared/bank_access';

export const DELETED_VIEW = 'deleted';

// The payouts routes' roles (goserver canReviewPayoutDetails): anyone else would only collect 403s.
export const can_see_deleted_accounts = can_see_bank_details;
export const PAYOUT_VIEW_KEYS = ['file', 'response', 'held', 'claims', DELETED_VIEW, 'holidays'];
// Where every banner points: the Bank payouts tab opened on its Deleted accounts section.
export const DELETED_ACCOUNTS_PATH = '/teacher-payments/bank-payouts';
export const DELETED_ACCOUNTS_LINK = `${DELETED_ACCOUNTS_PATH}?view=${DELETED_VIEW}`;

const count_of = (v) => (Number.isInteger(v) && v > 0 ? v : 0);

// GET /payouts/deleted_accounts/summary -> {count, complete}; anything malformed reads as nothing due.
export const deleted_summary_view = (data) => ({
  count: count_of(data && data.count),
  complete: !data || data.complete !== false
});

// 403/404 mean "not for you / not deployed": nothing to show. Anything else (5xx, no answer) is
// unknown, so neither alert can silently vanish.
const read_unknown = (e) => {
  const status = e && e.response && e.response.status;
  return status !== 403 && status !== 404;
};

export const DELETED_UNKNOWN_TEXT =
  'Could not check deleted accounts for money due. Reload the page to check again.';

// A failed summary read, as the hook holds it.
export const deleted_summary_failed = (e) =>
  read_unknown(e) ? { count: 0, complete: true, unknown: true } : { count: 0, complete: true };

// The page banner, or null when nothing is due. A partial server scan means "at least N".
export const deleted_banner_text = (view) => {
  if (view && view.unknown === true) return DELETED_UNKNOWN_TEXT;
  const n = count_of(view && view.count);
  if (!n) return null;
  const shown = view.complete === false ? `${n}+` : String(n);
  return n === 1 && view.complete !== false
    ? '1 deleted account has money due'
    : `${shown} deleted accounts have money due`;
};

// "Deleted accounts (3)" on the section button; the plain label when none.
export const deleted_section_label = (label, count) =>
  count_of(count) ? `${label} (${count_of(count)})` : label;

// ?view= on the Bank payouts URL, whitelisted; null when absent or unknown.
export const payouts_view_from_search = (search) => {
  const v = new URLSearchParams(typeof search === 'string' ? search : '').get('view');
  return PAYOUT_VIEW_KEYS.indexOf(v) !== -1 ? v : null;
};

// GET /payouts/deleted_accounts/teacher -> the profile banner text, or null (not deleted, or nothing due).
export const deleted_teacher_banner = (data) => {
  const due = data && data.money_due;
  if (!data || data.deleted !== true || !due || due.has_money_due !== true) return null;
  if (typeof due.total !== 'number' || due.total <= 0) return null;
  const day = ist_day_label(data.deleted_on);
  return day
    ? `Account deleted on ${day} with ${fmt_money(due.total)} due`
    : `Account deleted with ${fmt_money(due.total)} due`;
};

// A failed profile read: the warning to show, or null (no grant / not found).
export const deleted_teacher_failed_text = (e) =>
  read_unknown(e)
    ? 'Could not check whether this account has deleted-account money due. Reload to check again.'
    : null;
